/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";
import PhoneInput from "react-phone-input-2";
import Label from "../Label";
import { useController } from "react-hook-form";
import { useTranslate } from "../../../hooks/useTranslate";


const StyledPhoneInput = styled((props) => <PhoneInput {...props} />)`
  && {
    font-family: inherit;

    .form-control {
      width: 100%;
      height: auto;
      ${(props) => props.disableDropdown && "padding: 7px 16px 7px 64px;"}
      ${(props) => !props.disableDropdown && "padding: 7px 16px 7px 84px;"}
      font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
      line-height: 1.43;
      border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
      border-radius: 8px;
      color: rgba(${({ theme }) => theme.colors.text.rgba});
      background: transparent;

      ${(props) =>
        props.$error &&
        css`
          border-color: rgba(${props.theme.colors.error.rgba});
        `}

      /* library sets outline: none here; roll back to the browser's own focus ring */
      &:focus-visible {
        outline: revert;
        outline-offset: revert;
      }
    }

    .flag-dropdown {
      border: none;
      border-right: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
      background: transparent;

      ${(props) =>
        props.$error &&
        css`
          border-color: rgba(${props.theme.colors.error.rgba});
        `}

      &.open {
        z-index: 1;
      }
    }

    .selected-flag {
      padding: 0 16px;
      ${(props) => !props.disableDropdown && "width: 76px;"}
      ${(props) => props.disableDropdown && "width: 56px;"}

      &.open,
      &:focus {
        border-radius: 0;
        background: transparent;
      }

      /* library sets outline: none here; roll back to the browser's own focus ring */
      &:focus-visible {
        outline: revert;
        outline-offset: revert;
      }

      @media (hover: hover) {
        &:hover {
          border-radius: 0;
          background: transparent;
        }
      }

      &.open {
        z-index: 1;
      }

      .arrow {
        width: 24px;
        height: 24px;
        margin-top: 2px;
        top: 50%;
        transform: translateY(-50%);
        right: -24px;
        border: none;
        background-image: url('data:image/svg+xml, <svg viewBox="0 0 24 24" fill="${({ theme }) =>
          theme.colors
            .textSecondary}" xmlns="http://www.w3.org/2000/svg"><path d="M11.7188 14.7461L7.64062 10.5977C7.54688 10.5273 7.5 10.4336 7.5 10.3164C7.5 10.1992 7.53516 10.0938 7.60547 10L7.88672 9.75391C7.95703 9.66016 8.05078 9.61328 8.16797 9.61328C8.28516 9.61328 8.39062 9.66016 8.48438 9.75391L12 13.3398L15.5156 9.75391C15.6094 9.68359 15.7148 9.64844 15.832 9.64844C15.9492 9.64844 16.043 9.68359 16.1133 9.75391L16.3945 10C16.4648 10.0938 16.5 10.1992 16.5 10.3164C16.5 10.4336 16.4648 10.5273 16.3945 10.5977L12.3164 14.7461C12.2227 14.8164 12.1172 14.8516 12 14.8516C11.8828 14.8516 11.7773 14.8164 11.6836 14.7461H11.7188Z" /></svg>');

        &.up {
          border: none;
          transform: translateY(-50%) rotate(180deg);
        }
      }
    }

    .country-list {
      box-shadow: none;
      border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
      border-radius: 8px;

      /* the list itself gets programmatically focused on open (tabindex="0"), so it needs a ring too */
      &:focus-visible {
        outline: revert;
        outline-offset: revert;
      }

      .search-box {
        &:focus-visible {
          outline: revert;
          outline-offset: revert;
        }
      }
    }
  }
`;

const PhoneField = React.forwardRef(
  (
    {
      name,
      control,
      label,
      required = false,
      prefixChangable,
      defaultValue,
      readOnly,
      validationRules = {},
      inputProps,
      placeholder,
      ...props
    },
    ref
  ) => {
    const { _t } = useTranslate();
    const { field, fieldState } = useController({
      name,
      control,
      rules: {
        required: required ? "error_value_is_empty" : undefined,
        pattern: {
          value: /^(\+\d{1,3}[- ]?)?(\d{2,3}-?){3,5}\d{1,2}$/,
          message: "error_value_is_not_phone",
          ...validationRules,
        },
      },
      defaultValue: defaultValue || "",
    });
    return (
      <>
        {label ? (
          <Label name={name} required={required}>
            {label}
          </Label>
        ) : undefined}
        <StyledPhoneInput
          {...field}
          $error={fieldState.error}
          disableDropdown={!prefixChangable}
          countryCodeEditable={prefixChangable}
          id={name}
          value={defaultValue}
          ref={ref}
          placeholder={_t(placeholder)}
          inputProps={{
            readOnly,
            "aria-invalid": !!fieldState.error,
            "aria-required": required || undefined,
            "aria-describedby": fieldState.error ? `${name}-error` : undefined,
            ...inputProps,
          }}
          {...props}
        />
      </>
    );
  }
);

export default PhoneField;
